import * as React from 'react';
import {
    BooleanInput,
    Create,
    Datagrid,
    Edit,
    EditButton,
    FormDataConsumer,
    ImageField,
    ImageInput,
    List,
    ReferenceArrayInput,
    required,
    SelectArrayInput,
    SelectInput,
    SimpleForm,
    TextField,
    TextInput,
    useDataProvider,
    useInput,
    useListContext,
    useNotify,
    useRedirect,
    useRecordContext,
    useRefresh,
    FunctionField
} from 'react-admin';
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    Typography,
} from '@material-ui/core';
import AdminPagination from '../AdminPagination';
import AdminSelectableDatagrid from '../AdminSelectableDatagrid';
import { ResourceListTitle } from '../AdminResourceTotalCount';
import HumanDeleteButton from '../HumanDeleteButton';
import { silentAdminRequest } from '../providers/silentHttpClient';

const userFilters = [
    <TextInput
        source="all"
        label="Rechercher"
        alwaysOn
    />,
    <SelectInput
        source="promote"
        label="Mise en avant"
        choices={[
            { id: 'new', name: 'Oui' },
            { id: 'none', name: 'Non' },
        ]}
    />,
];

const catalogSongFilters = [
    <TextInput
        source="all"
        label="Rechercher"
        alwaysOn
    />,
];

const SongEditTitle = (props) => {
    const record = useRecordContext(props);

    if (!record) {
        return <span>Song</span>;
    }

    const artist = String(record.artist || '').trim();
    const title = String(record.title || '').trim();

    const label =
        artist && title
            ? `${artist} — ${title}`
            : artist || title || 'Song';

    return <span>{label}</span>;
};



const normalizeCatalogId = (value: any) => {
    if (value === undefined || value === null) return '';
    if (typeof value === 'string') return value;
    if (value._id !== undefined && value._id !== null) {
        return String(value._id);
    }
    if (value.id !== undefined && value.id !== null) {
        return String(value.id);
    }
    return String(value);
};

const GeneralCatalogInput = ({
    defaultChecked = false,
    isDemo = false,
}: any) => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();
    const { input } = useInput({ source: 'catalog_ids' });
    const [generalCatalogId, setGeneralCatalogId] = React.useState('');
    const defaultApplied = React.useRef(false);

    React.useEffect(() => {
        let mounted = true;

        dataProvider.getList('catalog', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'name', order: 'ASC' },
            filter: {},
        }).then((response: any) => {
            if (!mounted) return;

            const general = (response.data || []).find(
                (catalog: any) => catalog.slug === 'general'
            );

            if (!general) {
                notify('Catalogue Général introuvable', 'warning');
                return;
            }

            setGeneralCatalogId(String(general.id));
        }).catch(() => {
            if (mounted) {
                notify('Impossible de charger le Catalogue Général', 'warning');
            }
        });

        return () => {
            mounted = false;
        };
    }, [dataProvider, notify]);

    React.useEffect(() => {
        if (
            !generalCatalogId ||
            defaultApplied.current ||
            !defaultChecked ||
            isDemo
        ) {
            return;
        }

        const current = Array.isArray(input.value) ? input.value : [];
        const alreadyPresent = current.some(
            (value: any) => normalizeCatalogId(value) === generalCatalogId
        );

        if (!alreadyPresent) {
            input.onChange([...current, generalCatalogId]);
        }

        defaultApplied.current = true;
    }, [defaultChecked, generalCatalogId, input, isDemo]);

    React.useEffect(() => {
        if (!generalCatalogId || !isDemo) return;

        const current = Array.isArray(input.value) ? input.value : [];
        const withoutGeneral = current.filter(
            (value: any) => normalizeCatalogId(value) !== generalCatalogId
        );

        if (withoutGeneral.length !== current.length) {
            input.onChange(withoutGeneral);
        }
    }, [generalCatalogId, input, isDemo]);

    const current = Array.isArray(input.value) ? input.value : [];
    const checked = !!generalCatalogId && current.some(
        (value: any) => normalizeCatalogId(value) === generalCatalogId
    );

    const toggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!generalCatalogId) return;

        const values = Array.isArray(input.value) ? input.value : [];

        if (event.target.checked) {
            const withoutDuplicate = values.filter(
                (value: any) => normalizeCatalogId(value) !== generalCatalogId
            );
            input.onChange([...withoutDuplicate, generalCatalogId]);
        } else {
            input.onChange(
                values.filter(
                    (value: any) => normalizeCatalogId(value) !== generalCatalogId
                )
            );
        }
    };

    return (
        <label
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                marginBottom: 8,
            }}
        >
            <input
                type="checkbox"
                checked={checked}
                disabled={!generalCatalogId || isDemo}
                onChange={toggle}
            />
            <span>Catalogue Général</span>
        </label>
    );
};

const PreviewImage = ({ record = null, source }: any) => {
    if (typeof record == 'string') {
        record = {
            [source]: record + `?=${Math.random()}`,
        };
    }
    return <ImageField record={record} source={source} />;
};

const hasFilledValue = (value: any) => {
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }

    return value !== null &&
        value !== undefined &&
        value !== false &&
        value !== '';
};

const isSacemComplete = (record: any) =>
    hasFilledValue(record.composer_credit) &&
    hasFilledValue(record.composer_credit2) &&
    hasFilledValue(record.iswc) &&
    hasFilledValue(record.sacem_title);

const StatusDot = ({
    ok,
    title,
}: {
    ok: boolean;
    title: string;
}) => (
    <span
        title={title}
        aria-label={title}
        style={{
            display: 'inline-flex',
            width: 20,
            height: 20,
            minWidth: 20,
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: ok ? '#2e7d32' : '#d32f2f',
            color: '#fff',
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
        }}
    >
        {ok ? '✓' : '×'}
    </span>
);

const SongFeaturedBulkActions = () => {
    const context: any = useListContext();
    const notify = useNotify();

    const selectedIds: any[] =
        context?.selectedIds || [];

    const [saving, setSaving] =
        React.useState(false);

    const setFeatured = async (
        featured: boolean
    ) => {
        if (!selectedIds.length || saving) return;

        setSaving(true);

        try {
            await Promise.all(
                selectedIds.map((id: any) =>
                    silentAdminRequest(
                        `/song/${id}/featured`,
                        {
                            method: 'PATCH',
                            body: JSON.stringify({
                                featured,
                            }),
                        }
                    )
                )
            );

            // Met à jour les lignes déjà chargées sans
            // rafraîchir/reclasser toute la liste.
            selectedIds.forEach((id: any) => {
                const row = context?.data?.[id];

                if (row) {
                    row.promote =
                        featured
                            ? 'popular'
                            : undefined;
                }
            });

            notify(
                featured
                    ? `${selectedIds.length} chanson(s) mise(s) en avant`
                    : `${selectedIds.length} chanson(s) retirée(s) de la mise en avant`,
                'success'
            );

            if (context?.onUnselectItems) {
                context.onUnselectItems();
            }
        } catch (error) {
            notify(
                'Impossible de modifier les mises en avant',
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button
                color="primary"
                size="small"
                disabled={saving}
                onClick={() => setFeatured(true)}
            >
                Mettre en avant
            </Button>

            <Button
                color="secondary"
                size="small"
                disabled={saving}
                onClick={() => setFeatured(false)}
            >
                Retirer de la mise en avant
            </Button>
        </>
    );
};

const SongFeaturedCheckbox = ({
    record,
}: any) => {
    const notify = useNotify();

    const recordChecked =
        record?.promote === 'new' ||
        record?.promote === 'popular';

    const [checked, setChecked] =
        React.useState(recordChecked);
    const [saving, setSaving] =
        React.useState(false);

    React.useEffect(() => {
        setChecked(recordChecked);
    }, [recordChecked]);

    const toggle = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        if (!record?.id || saving) return;

        const featured = event.target.checked;
        const previous = checked;

        // Retour visuel immédiat, sans recharger la liste.
        setChecked(featured);
        setSaving(true);

        try {
            await silentAdminRequest(
                `/song/${record.id}/featured`,
                {
                    method: 'PATCH',
                    body: JSON.stringify({
                        featured,
                    }),
                }
            );

        } catch (error) {
            setChecked(previous);

            notify(
                'Impossible de modifier la mise en avant',
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Checkbox
            checked={checked}
            disabled={saving}
            onClick={(event) =>
                event.stopPropagation()
            }
            onChange={toggle}
            title={
                checked
                    ? 'Retirer de la mise en avant'
                    : 'Mettre en avant'
            }
        />
    );
};

export const SongList = (props) => {
    const demoMode = Boolean(props.demoMode);

    return (
    <List
        title={
            <ResourceListTitle
                resource="song"
                label={demoMode ? "Karaoké — Démo" : "Karaoké"}
                queryString={`is_demo=${demoMode}`}
            />
        }
        {...props}
        filter={{ is_demo: demoMode }}
        filters={demoMode ? catalogSongFilters : userFilters}
        bulkActionButtons={false}
        sort={{ field: 'artist', order: 'ASC' }}
        perPage={50}
        pagination={<AdminPagination />}
        exporter={false}
    >
        <AdminSelectableDatagrid
            selectionActions={
                demoMode
                    ? undefined
                    : () => <SongFeaturedBulkActions />
            }
        >
            <TextField source="artist" label="Artiste" />
            <TextField source="title" label="Titre" />

            {!demoMode && (
                <FunctionField
                    label="Mise en avant"
                    sortBy="promote"
                    render={(record: any) => (
                        <SongFeaturedCheckbox
                            record={record}
                        />
                    )}
                />
            )}

            <FunctionField
                label="Publié"
                sortBy="published"
                render={(record: any) => (
                    <StatusDot
                        ok={record.published === true}
                        title={
                            record.published === true
                                ? 'Publié'
                                : 'Non publié'
                        }
                    />
                )}
            />

            <FunctionField
                label="SACEM"
                sortBy="sacem_status"
                render={(record: any) => {
                    const ok = isSacemComplete(record);

                    return (
                        <StatusDot
                            ok={ok}
                            title={
                                ok
                                    ? 'Informations SACEM complètes'
                                    : 'Informations SACEM incomplètes'
                            }
                        />
                    );
                }}
            />

            <FunctionField
                label="Jaquette"
                sortBy="jaquette_status"
                render={(record: any) => {
                    const ok = hasFilledValue(record.imageURL);

                    return (
                        <StatusDot
                            ok={ok}
                            title={
                                ok
                                    ? 'Jaquette présente'
                                    : 'Jaquette absente'
                            }
                        />
                    );
                }}
            />

            <EditButton />
            <HumanDeleteButton fields={["artist", "title"]} fallback="cette chanson" />
        </AdminSelectableDatagrid>
    </List>
    );
};


const songBelongsToCatalog = (
    record: any,
    catalogId: string
) =>
    (record?.catalog_ids || []).some(
        (value: any) =>
            normalizeCatalogId(value) === String(catalogId)
    );

const CatalogSongSelectionTable = ({
    catalogId,
    eventId,
}: any) => {
    const context: any = useListContext();
    const dataProvider: any = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const refresh = useRefresh();

    const [saving, setSaving] = React.useState(false);
    const [catalogRecord, setCatalogRecord] =
        React.useState<any>(null);

    const catalogRecordRef =
        React.useRef<any>(null);

    const featuredWriteQueueRef =
        React.useRef<Promise<any>>(
            Promise.resolve()
        );

    const playlistWriteQueueRef =
        React.useRef<Promise<any>>(
            Promise.resolve()
        );

    const playlistMutationVersionRef =
        React.useRef<Record<string, number>>({});

    const [, bumpMembershipVersion] =
        React.useReducer(
            (value: number) => value + 1,
            0
        );

    const loadCatalog = React.useCallback(
        async (showError = true) => {
            if (!catalogId) return null;

            try {
                const response =
                    await dataProvider.getOne(
                        'catalog',
                        { id: catalogId }
                    );

                const record =
                    response?.data || null;

                setCatalogRecord(record);
                catalogRecordRef.current = record;

                return record;
            } catch (error) {
                if (showError) {
                    notify(
                        'Impossible de charger la configuration de la playlist',
                        'warning'
                    );
                }

                return null;
            }
        },
        [
            catalogId,
            dataProvider,
            notify,
        ]
    );

    React.useEffect(() => {
        loadCatalog();
    }, [loadCatalog]);

    const ids: any[] = context?.ids || [];
    const data: any = context?.data || {};

    const pageSongs = ids
        .map((id: any) => data[id])
        .filter(Boolean);

    const selectedOnPage = pageSongs.filter(
        (song: any) =>
            songBelongsToCatalog(song, catalogId)
    ).length;

    const customAllowed =
        catalogRecord?.slug !== 'general';

    const featuredMode =
        customAllowed &&
        catalogRecord?.featured_mode === 'custom'
            ? 'custom'
            : 'standard';

    const featuredSongIds = (
        Array.isArray(catalogRecord?.featured_song_ids)
            ? catalogRecord.featured_song_ids
            : []
    ).map((value: any) =>
        normalizeCatalogId(value)
    );

    const isCustomFeatured = (record: any) =>
        !!record?.id &&
        featuredSongIds.includes(String(record.id));

    const isStandardFeatured = (record: any) =>
        record?.promote === 'new' ||
        record?.promote === 'popular';

    const isDisplayedAsFeatured = (record: any) => {
        if (
            !songBelongsToCatalog(
                record,
                catalogId
            )
        ) {
            return false;
        }

        return featuredMode === 'custom'
            ? isCustomFeatured(record)
            : isStandardFeatured(record);
    };

    const featuredOnPage =
        pageSongs.filter(
            (song: any) =>
                isDisplayedAsFeatured(song)
        ).length;

    const updateCatalogFeatured = async (
        patch: any
    ) => {
        const previousRecord =
            catalogRecordRef.current ||
            catalogRecord || {
                id: catalogId,
            };

        const optimisticRecord = {
            ...previousRecord,
            ...patch,
            id:
                previousRecord?.id ||
                catalogId,
        };

        // Mise à jour immédiate de l'écran.
        // Aucun reload de la playlist.
        catalogRecordRef.current =
            optimisticRecord;
        setCatalogRecord(
            optimisticRecord
        );

        const write = () =>
            silentAdminRequest(
                `/catalog/${catalogId}`,
                {
                    method: 'PATCH',
                    body: JSON.stringify(patch),
                }
            );

        // Les clics rapides sont enregistrés dans l'ordre.
        const request =
            featuredWriteQueueRef.current.then(
                write,
                write
            );

        featuredWriteQueueRef.current =
            request.then(
                () => undefined,
                () => undefined
            );

        try {
            await request;
        } catch (error) {
            // Recharger uniquement en cas d'échec.
            await loadCatalog(false);
            throw error;
        }
    };

    const setFeaturedMode = async (
        mode: 'standard' | 'custom'
    ) => {
        if (
            saving ||
            !customAllowed ||
            mode === featuredMode
        ) {
            return;
        }

        setSaving(true);

        try {
            await updateCatalogFeatured({
                featured_mode: mode,
            });

            notify(
                mode === 'custom'
                    ? 'Mises en avant personnalisées activées'
                    : 'Mises en avant Standard activées',
                'success'
            );
        } catch (error) {
            notify(
                'Impossible de modifier le mode de mise en avant',
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    const toggleFeatured = async (
        record: any,
        checked: boolean
    ) => {
        if (
            !record?.id ||
            featuredMode !== 'custom' ||
            !songBelongsToCatalog(
                record,
                catalogId
            )
        ) {
            return;
        }

        const songId =
            String(record.id);

        const currentIds = (
            Array.isArray(
                catalogRecordRef.current
                    ?.featured_song_ids
            )
                ? catalogRecordRef.current
                    .featured_song_ids
                : []
        ).map((value: any) =>
            normalizeCatalogId(value)
        );

        const nextIds = checked
            ? Array.from(
                new Set([
                    ...currentIds,
                    songId,
                ])
            )
            : currentIds.filter(
                (id: string) =>
                    id !== songId
            );

        try {
            await updateCatalogFeatured({
                featured_song_ids:
                    nextIds,
            });

        } catch (error) {
            notify(
                'Impossible de modifier la mise en avant',
                'warning'
            );
        }
    };

    const toggleSong = async (
        record: any,
        checked: boolean
    ) => {
        if (!record?.id) return;

        const songId = String(record.id);

        const previousCatalogIds =
            Array.isArray(record.catalog_ids)
                ? [...record.catalog_ids]
                : [];

        const currentVersion =
            (
                playlistMutationVersionRef
                    .current[songId] || 0
            ) + 1;

        playlistMutationVersionRef
            .current[songId] =
            currentVersion;

        // Mise à jour visuelle immédiate.
        if (checked) {
            const alreadyPresent =
                previousCatalogIds.some(
                    (value: any) =>
                        normalizeCatalogId(value) ===
                        String(catalogId)
                );

            record.catalog_ids =
                alreadyPresent
                    ? previousCatalogIds
                    : [
                        ...previousCatalogIds,
                        catalogId,
                    ];
        } else {
            record.catalog_ids =
                previousCatalogIds.filter(
                    (value: any) =>
                        normalizeCatalogId(value) !==
                        String(catalogId)
                );
        }

        bumpMembershipVersion();

        const write = () =>
            silentAdminRequest(
                `/catalog/${catalogId}/songs/${
                    checked ? 'add' : 'remove'
                }`,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        song_ids: [record.id],
                    }),
                }
            );

        const request =
            playlistWriteQueueRef.current.then(
                write,
                write
            );

        playlistWriteQueueRef.current =
            request.then(
                () => undefined,
                () => undefined
            );

        try {
            await request;

            /*
             * Après un retrait, le backend peut aussi
             * nettoyer la mise en avant correspondante.
             * On resynchronise uniquement le Catalog,
             * silencieusement, sans recharger la List.
             */
            if (!checked) {
                const response =
                    await silentAdminRequest(
                        `/catalog/${catalogId}`
                    );

                if (response?.json) {
                    catalogRecordRef.current =
                        response.json;

                    setCatalogRecord(
                        response.json
                    );
                }
            }
        } catch (error) {
            /*
             * Ne restaurer l'ancien état que si aucun
             * clic plus récent n'a eu lieu sur cette ligne.
             */
            if (
                playlistMutationVersionRef
                    .current[songId] ===
                currentVersion
            ) {
                record.catalog_ids =
                    previousCatalogIds;

                bumpMembershipVersion();
            }

            notify(
                checked
                    ? 'Impossible d’ajouter cette chanson à la playlist'
                    : 'Impossible de retirer cette chanson de la playlist',
                'warning'
            );
        }
    };

    const setCurrentPage = async (
        checked: boolean
    ) => {
        if (!pageSongs.length || saving) return;

        const idsToChange = pageSongs
            .filter((song: any) =>
                checked
                    ? !songBelongsToCatalog(song, catalogId)
                    : songBelongsToCatalog(song, catalogId)
            )
            .map((song: any) => String(song.id));

        if (!idsToChange.length) return;

        setSaving(true);

        try {
            if (checked) {
                await dataProvider.addCatalogSongs(
                    catalogId,
                    idsToChange
                );
            } else {
                await dataProvider.removeCatalogSongs(
                    catalogId,
                    idsToChange
                );

                await loadCatalog(false);
            }

            notify(
                checked
                    ? `${idsToChange.length} chanson(s) ajoutée(s) à la playlist`
                    : `${idsToChange.length} chanson(s) retirée(s) de la playlist`,
                'success'
            );

            refresh();
        } catch (error) {
            notify(
                'Impossible de modifier la sélection',
                'warning'
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Box
                mb={2}
                p={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                style={{
                    gap: 12,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: 4,
                }}
            >
                <Box
                    style={{
                        width: '100%',
                        paddingBottom: 10,
                        borderBottom: '1px solid #eee',
                    }}
                >
                    <Typography variant="subtitle2">
                        Mises en avant de cette playlist
                    </Typography>

                    {customAllowed ? (
                        <>
                            <RadioGroup
                                row
                                value={featuredMode}
                                onChange={(event) =>
                                    setFeaturedMode(
                                        event.target.value as
                                            | 'standard'
                                            | 'custom'
                                    )
                                }
                            >
                                <FormControlLabel
                                    value="standard"
                                    control={
                                        <Radio
                                            color="primary"
                                            disabled={saving}
                                        />
                                    }
                                    label="Standard"
                                />

                                <FormControlLabel
                                    value="custom"
                                    control={
                                        <Radio
                                            color="primary"
                                            disabled={saving}
                                        />
                                    }
                                    label="Personnalisées"
                                />
                            </RadioGroup>

                            <Typography
                                variant="body2"
                                color="textSecondary"
                            >
                                {featuredMode === 'custom'
                                    ? 'Cette playlist utilise ses propres mises en avant.'
                                    : 'Cette playlist reprend les mises en avant Standard parmi les chansons qu’elle contient.'}
                            </Typography>
                        </>
                    ) : (
                        <Typography
                            variant="body2"
                            color="textSecondary"
                        >
                            Le Catalogue Général utilise les mises en avant Standard.
                        </Typography>
                    )}
                </Box>

                <Typography variant="body2">
                    <strong>
                        {selectedOnPage}
                    </strong>
                    {' '}chanson(s) de cette page dans la playlist
                    {' — '}
                    <strong>
                        {featuredOnPage}
                    </strong>
                    {' '}mise(s) en avant
                </Typography>

                <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    style={{ gap: 8 }}
                >
                    {eventId && (
                        <>
                            <Typography
                                variant="body2"
                                color="textSecondary"
                            >
                                Enregistrement automatique
                            </Typography>

                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() =>
                                    redirect(`/event/${eventId}`)
                                }
                            >
                                Terminer et revenir à l’événement
                            </Button>
                        </>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={
                            saving ||
                            !pageSongs.length ||
                            selectedOnPage === pageSongs.length
                        }
                        onClick={() => setCurrentPage(true)}
                    >
                        Tout cocher cette page
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        size="small"
                        disabled={
                            saving ||
                            selectedOnPage === 0
                        }
                        onClick={() => setCurrentPage(false)}
                    >
                        Tout décocher cette page
                    </Button>


                </Box>
            </Box>

            <Datagrid>
                <FunctionField
                    label="Playlist"
                    render={(record: any) => (
                        <Checkbox
                            checked={
                                songBelongsToCatalog(
                                    record,
                                    catalogId
                                )
                            }
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                            onChange={(event) =>
                                toggleSong(
                                    record,
                                    event.target.checked
                                )
                            }
                        />
                    )}
                />

                <FunctionField
                    label="Mise en avant"
                    render={(record: any) => {
                        const belongs =
                            songBelongsToCatalog(
                                record,
                                catalogId
                            );

                        return (
                            <Checkbox
                                checked={
                                    belongs &&
                                    isDisplayedAsFeatured(
                                        record
                                    )
                                }
                                disabled={
                                    featuredMode !== 'custom' ||
                                    !belongs
                                }
                                onClick={(event) =>
                                    event.stopPropagation()
                                }
                                onChange={(event) =>
                                    toggleFeatured(
                                        record,
                                        event.target.checked
                                    )
                                }
                                title={
                                    featuredMode === 'custom'
                                        ? belongs
                                            ? 'Mise en avant dans cette playlist'
                                            : 'Ajoutez d’abord la chanson à la playlist'
                                        : 'Mise en avant Standard'
                                }
                            />
                        );
                    }}
                />

                <TextField
                    source="artist"
                    label="Artiste"
                />

                <TextField
                    source="title"
                    label="Titre"
                />

                <FunctionField
                    label="Publié"
                    sortBy="published"
                    render={(record: any) => (
                        <StatusDot
                            ok={record.published === true}
                            title={
                                record.published === true
                                    ? 'Publié'
                                    : 'Non publié'
                            }
                        />
                    )}
                />

                <FunctionField
                    label="SACEM"
                    sortBy="sacem_status"
                    render={(record: any) => {
                        const ok =
                            isSacemComplete(record);

                        return (
                            <StatusDot
                                ok={ok}
                                title={
                                    ok
                                        ? 'Informations SACEM complètes'
                                        : 'Informations SACEM incomplètes'
                                }
                            />
                        );
                    }}
                />

                <FunctionField
                    label="Jaquette"
                    sortBy="jaquette_status"
                    render={(record: any) => {
                        const ok =
                            hasFilledValue(
                                record.imageURL
                            );

                        return (
                            <StatusDot
                                ok={ok}
                                title={
                                    ok
                                        ? 'Jaquette présente'
                                        : 'Jaquette absente'
                                }
                            />
                        );
                    }}
                />
            </Datagrid>
        </>
    );
};

export const CatalogSongSelector = (
    props: any
) => {
    const dataProvider: any = useDataProvider();
    const notify = useNotify();

    const catalogId = String(
        props.catalogId || ''
    );

    const [
        catalogName,
        setCatalogName,
    ] = React.useState('');

    React.useEffect(() => {
        if (!catalogId) return;

        let mounted = true;

        dataProvider
            .getOne('catalog', {
                id: catalogId,
            })
            .then((response: any) => {
                if (!mounted) return;

                setCatalogName(
                    response?.data?.name ||
                    'Playlist Catalogue'
                );
            })
            .catch(() => {
                if (mounted) {
                    notify(
                        'Impossible de charger la playlist',
                        'warning'
                    );
                }
            });

        return () => {
            mounted = false;
        };
    }, [
        catalogId,
        dataProvider,
        notify,
    ]);

    return (
        <List
            {...props}
            title={
                catalogName
                    ? `Playlist Catalogue — ${catalogName}`
                    : 'Playlist Catalogue'
            }
            filter={{
                is_demo: false,
            }}
            filters={catalogSongFilters}
            bulkActionButtons={false}
            actions={false}
            sort={{
                field: 'artist',
                order: 'ASC',
            }}
            perPage={50}
            pagination={<AdminPagination />}
            exporter={false}
        >
            <CatalogSongSelectionTable
                catalogId={catalogId}
                eventId={props.eventId}
            />
        </List>
    );
};

export const SongEdit = (props) => (
    <Edit {...props} mutationMode="pessimistic" title={<SongEditTitle />}>
        <SimpleForm
            redirect={props.basePath || 'list'}
            sanitizeEmptyValues={false}
        >
            <TextInput source="artist" label="Artiste" validate={required()} />
            <TextInput source="title" label="Titre" validate={required()} />
            <TextInput source="preview" label="Aperçu" validate={required()} />
            <TextInput source="video" label="Vidéo" validate={required()} />
            <TextInput source="description" label={'Tags'} />
            <TextInput source="composer_credit" label={'Crédit compositeur'}  />
            <TextInput source="composer_credit2" label={'Crédit auteur'}  />
            <TextInput source="iswc" label="ISWC"  />
            <TextInput source="sacem_title" label="Titre SACEM"  />

            <BooleanInput source="is_demo" label="Démo" />
            <BooleanInput source="published" label="Publié" />
            <FormDataConsumer>
                {({ formData }: any) => (
                    <GeneralCatalogInput
                        isDemo={Boolean(formData && formData.is_demo)}
                    />
                )}
            </FormDataConsumer>
            <SelectInput
                validate={required()}
                source="promote" label="Mise en avant"
                choices={[
                    { id: 'none', name: 'Aucune' },
                    { id: 'new', name: 'Nouveau' },
                    
                ]}
            />
            <ReferenceArrayInput source="songgenre_ids" label="Genres" reference="songgenre" allowEmpty>
                <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
            <ReferenceArrayInput source="songregion_ids" label="Régions" reference="songregion" allowEmpty>
                <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
            <ReferenceArrayInput source="songlang_ids" label="Langues" reference="songlang" allowEmpty>
                <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
            <SelectInput
                validate={required()}
                source="groupType" label="Type de groupe"
                choices={[
                    { id: 'solo', name: 'Solo' },
                    { id: 'duo', name: 'Duo' },
                    { id: 'group', name: 'Groupe' },
                ]}
            />
            <SelectInput
                validate={required()}
                source="voiceType" label="Type de voix"
                choices={[
                    { id: 'male', name: 'Homme' },
                    { id: 'female', name: 'Femme' },
                    { id: 'both', name: 'Homme / Femme' },
                ]}
            />
            <ImageInput source="imageURL" accept="image/*">
                {/* <ImageField source="src" title="title" /> */}
                <PreviewImage source="src" />
            </ImageInput>
        </SimpleForm>
    </Edit>
);

export const SongCreate = (props) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();
    const songSuggestionId =
        props.location &&
        props.location.state &&
        props.location.state.songSuggestionId;

    const handleSuccess = async (response) => {
        if (!songSuggestionId) {
            notify('ra.notification.created', 'info', { smart_count: 1 });
            redirect('list', props.basePath || '/song', response.data.id, response.data);
            return;
        }

        try {
            await dataProvider.completeSongSuggestion(
                songSuggestionId,
                response.data.id,
            );

            notify('La suggestion est créée', 'success');
            redirect('list', '/song-suggestion');
        } catch (error) {
            notify(
                'Le Song est créé, mais la suggestion n’a pas pu être archivée.',
                'warning',
            );
        }
    };

    return (
    <Create {...props} onSuccess={handleSuccess}>
        <SimpleForm redirect="list">
            <TextInput source="artist" label="Artiste" validate={required()} />
            <TextInput source="title" label="Titre" validate={required()} />
            <TextInput source="preview" label="Aperçu" validate={required()} />
            <TextInput source="video" label="Vidéo" validate={required()} />
            <TextInput source="description" label={'Tags'} />
            <TextInput source="composer_credit" label={'Crédit compositeur'}  />
            <TextInput source="composer_credit2"  label={'Crédit auteur'}  />
            <TextInput source="iswc" label="ISWC"  />
            <TextInput source="sacem_title" label="Titre SACEM"   />
            <BooleanInput source="published" label="Publié" validate={required()} defaultValue={false} />
            <BooleanInput
                source="is_demo"
                label="Démo"
                validate={required()}
                defaultValue={Boolean(props.demoMode)}
            />
            <FormDataConsumer>
                {({ formData }: any) => (
                    <GeneralCatalogInput
                        defaultChecked={!Boolean(props.demoMode)}
                        isDemo={Boolean(formData && formData.is_demo)}
                    />
                )}
            </FormDataConsumer>
            <SelectInput
                validate={required()}
                source="promote" label="Mise en avant"
                choices={[
                    { id: 'none', name: 'Aucune' },
                    { id: 'new', name: 'Nouveau' },
                    
                ]}
            />
            <ReferenceArrayInput source="songgenre_ids" reference="songgenre" allowEmpty label="Genre">
                <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
            <ReferenceArrayInput source="songregion_ids" reference="songregion" allowEmpty label="Région">
                <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
            <ReferenceArrayInput source="songlang_ids" reference="songlang" allowEmpty label="Langue">
                <SelectArrayInput optionText="name" />
            </ReferenceArrayInput>
            <SelectInput
                validate={required()}
                source="groupType" label="Type de groupe"
                choices={[
                    { id: 'solo', name: 'Solo' },
                    { id: 'duo', name: 'Duo' },
                    { id: 'group', name: 'Groupe' },
                ]}
            />
            <SelectInput
                validate={required()}
                source="voiceType" label="Type de voix"
                choices={[
                    { id: 'male', name: 'Homme' },
                    { id: 'female', name: 'Femme' },
                    { id: 'both', name: 'Homme / Femme' },
                ]}
            />
            <ImageInput source="imageURL" accept="image/*">
                <PreviewImage source="src" />
            </ImageInput>
        </SimpleForm>
    </Create>
    );
};
